import { t } from "@lingui/macro";
import {
  MarkProformaAsUnpaidDocument,
  type MarkProformaAsUnpaidMutation,
  type MarkProformaAsUnpaidMutationVariables,
  ModelLockLockableTypeEnum,
  OiProformaWhereColumn,
  OutgoingProformaInvoiceOrderByColumnEnum,
  PaymentStatusEnum,
  type ProformaInvoicesOptionsQuery,
  type ProformaInvoicesTableDataQuery,
  ProformaOmegaExportDocument,
  type ProformaOmegaExportQuery,
  type ProformaOmegaExportQueryVariables,
  ProformaPohodaExportDocument,
  type ProformaPohodaExportQuery,
  type ProformaPohodaExportQueryVariables,
  ProformaPremierExportDocument,
  type ProformaPremierExportQuery,
  type ProformaPremierExportQueryVariables,
  SortOrder,
  SqlOperator,
} from "@src/__generated__/graphql";
import { BankStore } from "@src/components/modules/bank/stores";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { appStore } from "@src/stores/AppStore";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import {
  getBooleanFilterOptions,
  getPaymentStatusFilterOptions,
} from "@src/utils/components/filters/constants";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import {
  createActiveQueryParam,
  parseActiveQueryParam,
} from "@src/utils/routing";
import { action, computed, makeObservable, observable } from "mobx";
import type { Query } from "nextjs-routes";
import mapToOptions from "../../../../../utils/map-to-options";
import { ChangeDatesModalStore } from "../../shared/change-dates-modal/store";
import type { ProformaInvoice, ProformaInvoicesSummary } from "../Models.types";
import { QuickFiltersEnum } from ".";

type MarkAsUnpaidModalData = {
  id: string;
  hasMatchedPayments: boolean;
};

export class Store {
  changeDatesModalStore = new ChangeDatesModalStore();
  markAsUnpaidModal = new DisclosureState<MarkAsUnpaidModalData>();

  // Table data
  @observable.ref proformaInvoicesSummary?: ProformaInvoicesSummary;
  @observable.ref proformaInvoices: ProformaInvoice[] = [];
  proformaInvoicesRefetch?: () => any;

  // Filtering
  @observable searchTerm = "";
  @observable activeQuickFilter: QuickFiltersEnum = QuickFiltersEnum.All;
  @observable selectedInvoiceIds: ProformaInvoice["id"][] = [];

  private pohodaExportFetcher = new FetchHelper<
    ProformaPohodaExportQuery,
    ProformaPohodaExportQueryVariables
  >(ProformaPohodaExportDocument);

  private omegaExportFetcher = new FetchHelper<
    ProformaOmegaExportQuery,
    ProformaOmegaExportQueryVariables
  >(ProformaOmegaExportDocument);

  private premierExportFetcher = new FetchHelper<
    ProformaPremierExportQuery,
    ProformaPremierExportQueryVariables
  >(ProformaPremierExportDocument);

  private markAsUnpaidMutator = new MutationHelper<
    MarkProformaAsUnpaidMutation,
    MarkProformaAsUnpaidMutationVariables
  >(MarkProformaAsUnpaidDocument);

  pagination = new PaginationState("proformaInvoices");

  orderBy = new OrderBy<OutgoingProformaInvoiceOrderByColumnEnum>([
    {
      column: OutgoingProformaInvoiceOrderByColumnEnum.IssueDate,
      order: SortOrder.Desc,
    },
    {
      column: OutgoingProformaInvoiceOrderByColumnEnum.InvoiceNo,
      order: SortOrder.Desc,
    },
  ]);

  // Quick Filters
  quickFiltersWhere = new Filters<OiProformaWhereColumn>(
    [
      new Filter({
        column: OiProformaWhereColumn.PaymentStatus,
        operator: SqlOperator.In,
        title: "",
        options: [],
      }),
    ],
    { onChange: this.pagination.resetPage },
  );

  // Common Filters
  where = new Filters<OiProformaWhereColumn>(
    [
      new Filter({
        column: OiProformaWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Client/Brand`,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        column: OiProformaWhereColumn.WorkspaceCompanyId,
        operator: SqlOperator.In,
        title: t`Legal entity`,
        options: [],
      }),
      new Filter({
        column: OiProformaWhereColumn.WorkspaceBankAccountId,
        operator: SqlOperator.In,
        title: t`Agency bank account`,
        options: [],
      }),
      new Filter({
        column: OiProformaWhereColumn.OwnerId,
        operator: SqlOperator.In,
        title: t`Project Manager`,
        options: [],
        hasSelectAllOption: true,
      }),
      new Filter({
        column: OiProformaWhereColumn.PaymentStatus,
        operator: SqlOperator.In,
        title: t`Payment status`,
        options: getPaymentStatusFilterOptions(),
      }),
      new Filter({
        column: OiProformaWhereColumn.IsExported,
        operator: SqlOperator.Eq,
        title: t`Exported`,
        options: getBooleanFilterOptions(),
      }),
      new Filter({
        column: OiProformaWhereColumn.IssueDate,
        operator: SqlOperator.Between,
        title: t`Issue date`,
        dateRange: true,
        options: [],
      }),
      new Filter({
        column: OiProformaWhereColumn.DueDate,
        operator: SqlOperator.Between,
        title: t`Due date`,
        dateRange: true,
        options: [],
      }),
      new Filter({
        column: OiProformaWhereColumn.PaidAt,
        operator: SqlOperator.Between,
        title: t`Paid at`,
        dateRange: true,
        options: [],
      }),
      new Filter({
        column: OiProformaWhereColumn.AmountFrom,
        operator: SqlOperator.Gte,
        title: t`Amount from (with VAT)`,
        options: [],
        mutateToGql: BankStore.mutateAmountFilter,
      }),
      new Filter({
        column: OiProformaWhereColumn.AmountTo,
        operator: SqlOperator.Lte,
        title: t`Amount to (with VAT)`,
        options: [],
        mutateToGql: BankStore.mutateAmountFilter,
      }),
    ],
    {
      onChange: this.pagination.resetPage,
      tableKey: "proforma-invoices",
      orderBy: this.orderBy,
    },
  );

  constructor() {
    makeObservable(this);
  }

  @computed
  get isMarkingAsUnpaid(): boolean {
    return this.markAsUnpaidMutator.isLoading.value;
  }

  @action.bound
  async markAsUnpaid(removeMatchedPayments: boolean): Promise<void> {
    const id = this.markAsUnpaidModal.additionalData?.id;
    if (!id) return;

    const [, error] = await this.markAsUnpaidMutator.mutate({
      id,
      remove_matched_payments: removeMatchedPayments,
    });
    if (error) return;

    this.proformaInvoicesRefetch?.();
  }

  @computed
  get isPohodaExporting() {
    return this.pohodaExportFetcher.isLoading.value;
  }

  @computed get quickFilterPaymentStatus() {
    return this.quickFiltersWhere.filters.find(
      (f) => f.column === OiProformaWhereColumn.PaymentStatus,
    );
  }

  @computed get urlParams() {
    return {
      ...commonSearchParams(this),
      ...createActiveQueryParam(this.activeQuickFilter),
    };
  }

  @computed get queryVariables() {
    return commonQueryVariables(this, { where: this.quickFiltersWhere });
  }

  @action
  setDataFromQuery(data: ProformaInvoicesTableDataQuery) {
    if (data.outgoingProformaInvoices) {
      this.pagination.setFromPaginatorInfo(
        data.outgoingProformaInvoices.paginatorInfo,
      );
      this.proformaInvoices = data.outgoingProformaInvoices.data.map(
        (invoice) => ({
          ...invoice,
          paid: invoice.accountMoves
            ? invoice.accountMoves.reduce((prev, curr) => {
                return prev + curr.amount_in_move_currency;
              }, 0)
            : null,
        }),
      );
    }
  }

  @action
  toggleQuickFilter(quickFilter: QuickFiltersEnum) {
    const paymentStatusFilter = this.where.filtersByColumn.get(
      OiProformaWhereColumn.PaymentStatus,
    );

    switch (quickFilter) {
      case QuickFiltersEnum.Unpaid:
        this.activeQuickFilter = QuickFiltersEnum.Unpaid;
        this.quickFilterPaymentStatus?.setValue([
          PaymentStatusEnum.NotPaid,
          PaymentStatusEnum.PartiallyPaid,
        ]);
        paymentStatusFilter?.clear();
        paymentStatusFilter?.hide();
        break;
      case QuickFiltersEnum.All:
        this.activeQuickFilter = QuickFiltersEnum.All;
        this.quickFilterPaymentStatus?.clear();
        if (paymentStatusFilter) {
          paymentStatusFilter.hidden = false;
          paymentStatusFilter.clear();
        }
        break;
    }
  }

  @action
  setSearchTerm(value: string) {
    this.searchTerm = value;
  }

  @action
  setSelectedIds(ids: ProformaInvoice["id"][]) {
    this.selectedInvoiceIds = ids;
  }

  setOptions(data: ProformaInvoicesOptionsQuery) {
    this.where.filtersByColumn
      .get(OiProformaWhereColumn.OwnerId)!
      .setOptions(
        mapToOptions.projectManagers.toOptions(data.projectManagerSimpleMap),
      );
    this.where.filtersByColumn.get(OiProformaWhereColumn.BrandId)?.setOptions(
      data.clientsSimpleMap.map((i) => ({
        label: i.name,
        value: i.id,
        options: i.brands.map((j) => ({
          label: j.name,
          value: j.id,
        })),
      })),
    );

    this.where.filtersByColumn
      .get(OiProformaWhereColumn.WorkspaceCompanyId)
      ?.setOptions(
        mapToOptions.workspaceCompanies(appStore.workspaceStore.companies),
      );

    this.where.filtersByColumn
      .get(OiProformaWhereColumn.WorkspaceBankAccountId)
      ?.setOptions(
        appStore.workspaceStore.companies
          .flatMap(({ bankAccounts }) => bankAccounts)
          .filter(({ deleted }) => !deleted)
          .map(({ id, iban }) => ({ value: id, label: iban })),
      );
  }

  setQuickFilterFromURL(query: Query) {
    const active = parseActiveQueryParam(query);
    if (active === QuickFiltersEnum.Unpaid) {
      this.toggleQuickFilter(QuickFiltersEnum.Unpaid);
    } else {
      this.toggleQuickFilter(QuickFiltersEnum.All);
    }
  }

  @computed
  get isPohodaExportDisabled() {
    return this.selectedInvoiceIds.length === 0;
  }

  @action.bound
  async exportPohoda(ids: string[] = this.selectedInvoiceIds) {
    const [data, error] = await this.pohodaExportFetcher.fetch({
      outgoingProformaInvoiceIds: ids,
    });

    if (error || !data.pohodaOPIExport) return;

    window.open(data.pohodaOPIExport, "_blank");
  }

  @computed
  get isOmegaExportDisabled() {
    return this.selectedInvoiceIds.length === 0;
  }

  @computed
  get isOmegaExporting() {
    return this.omegaExportFetcher.isLoading.value;
  }

  @computed
  get isPremierExporting() {
    return this.premierExportFetcher.isLoading.value;
  }

  @computed
  get isPremierExportDisabled() {
    return this.selectedInvoiceIds.length === 0;
  }

  @computed
  get areExportsDisabled() {
    return this.selectedInvoiceIds.length === 0;
  }

  @action.bound
  async exportOmega(ids: string[] = this.selectedInvoiceIds) {
    const [data, error] = await this.omegaExportFetcher.fetch({
      ids,
    });

    if (error || !data.omegaProformaExport) return;

    window.location.href = data.omegaProformaExport;
    this.proformaInvoicesRefetch?.();
  }

  @action.bound
  async exportPremier(ids: string[] = this.selectedInvoiceIds) {
    const [data, error] = await this.premierExportFetcher.fetch({
      outgoing_proforma_invoice_ids: ids,
    });

    if (error || !data.premierProformaOIExport) return;

    const exportResponse = data.premierProformaOIExport.trim().toLowerCase();

    if (exportResponse === "ok") {
      appStore.UIStore.toast({
        title: t`Premier exported successfully`,
        status: "success",
      });
      return;
    }

    if (exportResponse === "error") {
      appStore.UIStore.toast({ status: "error" });
      return;
    }

    window.location.href = data.premierProformaOIExport;
  }

  @computed
  get isBulkLocking() {
    return appStore.modelLock.isBulkLocking;
  }

  @computed
  get isBulkUnlocking() {
    return appStore.modelLock.isBulkUnlocking;
  }

  @computed
  get isBulkLockToggling() {
    return this.isBulkLocking || this.isBulkUnlocking;
  }

  @action.bound async bulkLock(ids: string[]) {
    const [data, error] = await appStore.modelLock.bulkLockModel(
      ids,
      ModelLockLockableTypeEnum.OutgoingProformaInvoice,
    );

    if (error || !data?.bulkLockModel) return false;

    appStore.UIStore.toast({
      status: data.bulkLockModel.failed === 0 ? "success" : "warning",
      title: t`${data.bulkLockModel.total - data.bulkLockModel.failed} out of ${data.bulkLockModel.total} proforma invoices locked successfully`,
    });

    this.proformaInvoicesRefetch?.();
    this.selectedInvoiceIds = [];

    return true;
  }

  @action.bound async bulkUnlock(ids: string[]) {
    const [data, error] = await appStore.modelLock.bulkUnlockModel(
      ids,
      ModelLockLockableTypeEnum.OutgoingProformaInvoice,
    );

    if (error || !data?.bulkUnlockModel) return false;

    appStore.UIStore.toast({
      status: data.bulkUnlockModel.failed === 0 ? "success" : "warning",
      title: t`${data.bulkUnlockModel.total - data.bulkUnlockModel.failed} out of ${data.bulkUnlockModel.total} proforma invoices unlocked successfully`,
    });

    this.proformaInvoicesRefetch?.();
    this.selectedInvoiceIds = [];

    return true;
  }

  @action.bound async lockProformaInvoice(id: string) {
    const [data, error] = await appStore.modelLock.lockModel(
      id,
      ModelLockLockableTypeEnum.OutgoingProformaInvoice,
    );

    if (error || !data?.lockModel) return false;

    appStore.UIStore.toast({
      status: "success",
      title: t`Proforma invoice locked successfully`,
    });

    this.proformaInvoicesRefetch?.();

    return true;
  }

  @action.bound async unlockProformaInvoice(id: string) {
    const [data, error] = await appStore.modelLock.unlockModel(
      id,
      ModelLockLockableTypeEnum.OutgoingProformaInvoice,
    );

    if (error || !data?.unlockModel) return false;

    appStore.UIStore.toast({
      status: "success",
      title: t`Proforma invoice unlocked successfully`,
    });

    this.proformaInvoicesRefetch?.();

    return true;
  }
}
